<template>
  <!-- INTERACTIVE — a self-navigating chip for surfaces whose row is NOT itself
       a link (BoxTotalsBreakdown's plain list rows). A real <button> is valid
       there; it carries the accessible name and opens the issue card. -->
  <button
    v-if="interactive"
    type="button"
    class="decision-chip decision-chip--tappable"
    :class="`decision-chip--${variant}`"
    :aria-label="ariaLabel"
    @click="onClick"
  >
    <turn-pill :variant="variant" chevron :announce="false"></turn-pill>
  </button>

  <!-- DISPLAY — a non-interactive pill for surfaces whose ancestor is already
       the tap target (ItemCard's list-item anchor row-navigates). Must stay a
       <span> (via TurnPill) so it's valid HTML inside that anchor. announce is
       false here too: display mode is BY DEFINITION nested inside an already-
       labelled interactive ancestor (that's what makes it "display" mode), so
       role="status" would be the same nested-live-region redundancy the
       interactive branch above already avoids. -->
  <turn-pill
    v-else
    :variant="variant"
    chevron
    class="decision-chip"
    :announce="false"
  ></turn-pill>
</template>

<script setup>
/**
 * DecisionChip — the ONE chip that OPENS a member's item-issue card, shared by
 * both order surfaces so the pill visual and the /issues/:id/ navigation live in
 * a single place:
 *
 *   • ItemCard          → display mode; the whole list-item ROW navigates, so
 *                         the chip is a plain <span> (TurnPill) inside the anchor.
 *   • BoxTotalsBreakdown → interactive mode; rows aren't links, so the chip is a
 *                          tappable <button> that opens the card itself.
 *
 * It is no longer decision-only. Since interaction-v2 the backend serves an issue
 * in EVERY status, so a BEING-HANDLED item (issue `open`, leader's court) opens
 * the same card read-only — the leader's message plus the full timeline. That is
 * the whole point: an item the member can see is in trouble must be able to tell
 * them WHY, not just that. `variant` picks whose court the chip announces; the
 * component is otherwise identical in both.
 *
 * The visual is TurnPill (single source of the semantic tokens); the navigation
 * is useIssueNavigation.openIssue (single source of the route). Callers decide
 * WHETHER to render this at all via issueChipVariant, which returns null for an
 * item with nothing to open — a being-handled item with no typed issue behind it
 * gets a bare, chevron-less TurnPill instead.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TurnPill from '@/components/collection/TurnPill.vue';
import { useIssueNavigation } from '@/composables/useIssueNavigation.js';
import { pillLabelKey } from '@/constants/collectionIssue.js';

const props = defineProps({
  // Required for interactive mode: the issue id the chip navigates to.
  issueId: {
    type: [String, Number],
    default: null,
  },
  // false (default) → display-only <span>; true → self-navigating <button>.
  interactive: {
    type: Boolean,
    default: false,
  },
  // Item name folded into the button's accessible label — see ariaLabel below.
  // Required in interactive mode: several rows can carry a chip, so the name is
  // the only thing telling them apart for a screen-reader user.
  itemName: {
    type: String,
    default: '',
  },
  // Whose court the chip announces — 'member' («قرارك مطلوب», amber),
  // 'handled' («شغالين عليها», calm brand), or 'oos' («نواقص», muted).
  // The chip is no longer live-issue-only: since `lastIssueId` (ASK 8) an
  // out-of-stock item still has its issue to open, and the pill must keep
  // announcing the terminal state rather than borrowing the leader's court.
  variant: {
    type: String,
    default: 'member',
    validator: (v) => ['member', 'handled', 'oos'].includes(v),
  },
});

const { t } = useI18n({ useScope: 'global' });
const { openIssue } = useIssueNavigation();

// Every chip carries the chevron, in BOTH modes and BOTH courts, because the
// chip only renders at all when the issue is openable (issueChipVariant returns
// null otherwise). So here the chevron means exactly one thing — "this opens
// something" — and it is the ONLY thing distinguishing the two «شغالين عليها»
// pills a member can meet: this one, which opens the issue, and the bare
// TurnPill the surfaces render when there is no issue behind the pill.
//
// Not relied on: F7's own row chevron. An f7-list-item with `link` does paint
// one (framework7/components/list/list.less `.media-list .item-link
// .item-title-row:before`, RTL-mirrored there by swapping the glyph), but
// ItemCard sets link="#" unconditionally, so EVERY row has it — including rows
// that merely open the details sheet. It cannot signal what is new here.

const ariaLabel = computed(() => {
  // State first (what the member wants to know), then the action — a button
  // named only «شغالين عليها» tells a screen-reader user nothing about the fact
  // that activating it opens anything, and the chevron above is aria-hidden.
  const pill = t(pillLabelKey(props.variant));
  if (!props.itemName) return pill;
  return `${pill}: ${t('collection.pill.openDetails', { item: props.itemName })}`;
});

function onClick() {
  openIssue(props.issueId);
}
</script>

<style scoped>
/* Tappable variant: a bare button reset — TurnPill supplies the pill visual, so
   the amber palette is defined once (TurnPill.turn-pill--member), never here. */
.decision-chip--tappable {
  display: inline-flex;
  align-items: center;
  min-height: 44px; /* WCAG target size — the pill itself is shorter */
  padding: 0 8px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: var(--poolit-radius-full, 999px);
}

.decision-chip--tappable:active {
  filter: brightness(0.97);
}

/* Focus ring follows the variant's own hue so the chip never borrows the other
   court's colour — whose-court semantics are locked. The override is written as
   a COMPOUND selector (both classes) so it wins on specificity, not on source
   order: a locked invariant must not be breakable by reordering these rules or
   appending another .decision-chip--tappable rule below.
   Note --member has no rule of its own: the base tappable ring already IS the
   amber one, so adding a redundant duplicate would just be a second place to
   forget to update. */
.decision-chip--tappable:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ci-await-ring, rgba(201, 130, 10, 0.5));
}

.decision-chip--tappable.decision-chip--handled:focus-visible {
  box-shadow: 0 0 0 3px var(--ci-brand-ring, rgba(102, 33, 226, 0.5));
}

.decision-chip--tappable.decision-chip--oos:focus-visible {
  box-shadow: 0 0 0 3px var(--ci-muted-ring, rgba(55, 65, 81, 0.4));
}
</style>
